import { useEffect, useMemo, useRef, useState } from 'react'
import toolRecords from '../site/data/tool-records.json'
import compressorRecords from '../site/data/compressor-records.json'
import articles from './data/articles.json'
import { siteConfig } from '../site/config'
import {
  compareToolToCompressor,
  formatNumber,
  type ComparisonResult,
  type ConsumptionMode,
  type CompressorRecord,
  type ToolRecord,
} from '../utilities/compressor/compare'
import {
  isConsumptionModePublished,
  modeAfterDirectToolSelection,
} from '../utilities/compressor/mode-selection'
import { pluralizeToolType } from '../utilities/pluralize-tool-type'
import { affiliateClickEvent, commerceBlock } from './commerce'
import {
  browserPathWithSharedComparison,
  browserPathWithoutSharedComparison,
  copySharedComparisonLink,
  createSharedComparisonState,
  parseSharedComparison,
  publicSharedComparisonUrl,
  resultLinkCopiedEvent,
  sharedResultViewedEvent,
  utilityCompletedEvent,
  type SharedComparisonState,
} from './shareable-result'

type Article = {
  slug: string
  title: string
  description: string
  date: string
  tags: string[]
  readTime: string | null
  heroImage: string | null
  heroImageAlt: string
}

const tools = toolRecords as ToolRecord[]
const compressors = compressorRecords as CompressorRecord[]
const articleList = articles as Article[]
const sharedComparisonOptions = {
  tools: tools.map(({ id, model, legacyShareAliases }) => ({ id, model, legacyShareAliases })),
  compressors: compressors.map(({ id, model, legacyShareAliases }) => ({ id, model, legacyShareAliases })),
}
const recordCount = tools.length + compressors.length
const manufacturerCount = new Set([...tools, ...compressors].map((record) => record.brand)).size
const toolTypeCounts = [...tools.reduce((map, record) => map.set(record.toolType, (map.get(record.toolType) ?? 0) + 1), new Map<string, number>())]
const impactWrenchCount = tools.filter((record) => record.toolType === 'Impact wrench').length

type CopyStatus = 'idle' | 'copied' | 'failed'

function track(event: string, details: Record<string, string | number> = {}) {
  window.dispatchEvent(new CustomEvent('utility-publisher:event', { detail: { event, ...details } }))
  const analytics = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  analytics?.('event', event, details)
}

function Wordmark() {
  return (
    <a className="wordmark" href="/" aria-label={`${siteConfig.name} home`}>
      <img className="wordmark-mark" src="/brand-mark.svg" alt="" width={22} height={22} />
      <span>{siteConfig.name}</span>
    </a>
  )
}

/** A reserved, labelled, empty advertising placement with a fixed size so a later unit moves nothing. */
function AdSlot({ name, width, height, note, className = '' }: { name: string; width: number; height: number; note?: string; className?: string }) {
  return (
    <aside className={`ad-slot ${className}`.trim()} data-ad-slot={name} aria-label="Advertisement" style={{ maxWidth: width, minHeight: height }}>
      <span className="ad-slot-label">Advertisement</span>
      <span className="ad-slot-size mono">{width} × {height}</span>
      {note ? <span className="ad-slot-note">{note}</span> : null}
    </aside>
  )
}

/** Retailer links under a result. Renders nothing while `commerce.enabled` is false in site.json. */
function RetailerLinks({ result, tool, compressor }: { result: ComparisonResult; tool: ToolRecord; compressor: CompressorRecord }) {
  const block = commerceBlock(siteConfig.commerce, result, tool, compressor)
  if (!block) return null
  return (
    <div className="retail" data-kind={block.kind}>
      <p className="retail-heading">{block.heading}</p>
      <div className="retail-links">
        {block.links.map((link) => (
          <a
            key={link.itemId}
            className="retail-link"
            href={link.href}
            target="_blank"
            rel="sponsored nofollow noopener"
            onClick={() => {
              const event = affiliateClickEvent(block, link, siteConfig.commerce.retailer)
              track(event.name, event.details)
            }}
          >
            <span className="retail-link-item">{link.item === 'tool' ? 'Tool' : 'Compressor'}</span>
            <span className="retail-link-label">{link.label}</span>
            <span aria-hidden="true">↗</span>
          </a>
        ))}
      </div>
      <p className="retail-disclosure">{block.disclosure}</p>
    </div>
  )
}

/**
 * Email capture for the shop air sheet. Renders nothing until `newsletter.enabled` is true and
 * `newsletter.formAction` holds the provider's form endpoint. The sheet link travels as a hidden
 * field so the provider's welcome email can deliver it.
 */
function SheetSignup({ sheetUrl }: { sheetUrl: string }) {
  const { newsletter } = siteConfig
  if (!newsletter.enabled || !newsletter.formAction) return null
  return (
    <form
      className="signup"
      method="post"
      action={newsletter.formAction}
      target="_blank"
      onSubmit={() => track('newsletter_signup_submitted', { placement: 'result', offer: 'shop_air_sheet' })}
    >
      <div className="signup-copy">
        <p className="signup-kicker">Free printable</p>
        <p className="signup-title">Get this as a shop air sheet</p>
        <p className="signup-body">Both published figures, the pressure and wording each maker used, the source links, and the dates we checked them, on one printable page.</p>
      </div>
      <div className="signup-fields">
        <label className="signup-label" htmlFor="signup-email">Email</label>
        <input id="signup-email" className="signup-input" type="email" name="EMAIL" autoComplete="email" required placeholder="you@example.com" />
        <input type="hidden" name="SHEET_URL" value={sheetUrl} />
        <label className="signup-consent">
          <input type="checkbox" name="OPT_IN" value="1" required />
          <span>Email me the sheet and occasional Air Tool Match updates. Unsubscribe at any time. See the <a href="/privacy/">privacy page</a>.</span>
        </label>
        <button className="button button-primary" type="submit">Email me the sheet</button>
      </div>
    </form>
  )
}

const sheetDate = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })

/** The printable shop air sheet, opened from the emailed link (`/?…&view=sheet`). */
function ShopSheet({ tool, compressor, mode, quantity }: { tool: ToolRecord; compressor: CompressorRecord; mode: ConsumptionMode; quantity: number }) {
  const result = compareToolToCompressor({ tool, compressor, mode, quantity })
  const figure = (value: number | null) => (value == null ? 'Not published' : `${formatNumber(value)} CFM`)
  const matcherHref = `/?tool=${encodeURIComponent(tool.id)}&compressor=${encodeURIComponent(compressor.id)}&mode=${mode}&qty=${quantity}#matcher`
  return (
    <main className="sheet">
      <div className="sheet-actions">
        <a className="sheet-back" href={matcherHref}>Back to the matcher</a>
        <button className="button button-primary" type="button" onClick={() => window.print()}>Print or save as PDF</button>
      </div>
      <header className="sheet-head">
        <p className="sheet-kicker">{siteConfig.name} · shop air sheet</p>
        <h1>{tool.brand} {tool.model} and {compressor.brand} {compressor.model}</h1>
        <p className="sheet-sub">Every figure below is the maker's own, with the page it came from and the date we checked it.</p>
      </header>

      <section className="sheet-block">
        <h2>Tool</h2>
        <dl className="sheet-facts">
          <div><dt>Model</dt><dd>{tool.brand} {tool.model} ({tool.toolType.toLowerCase()})</dd></div>
          <div><dt>Average air use</dt><dd className="mono">{figure(tool.averageCfm)}</dd></div>
          <div><dt>Air use at load</dt><dd className="mono">{figure(tool.loadCfm)}</dd></div>
          <div><dt>Stated at</dt><dd className="mono">{tool.pressurePsi} PSI</dd></div>
          <div><dt>Source</dt><dd className="sheet-url">{tool.sourceUrl}</dd></div>
          <div><dt>Checked</dt><dd>{sheetDate(tool.checkedDate)}</dd></div>
        </dl>
      </section>

      <section className="sheet-block">
        <h2>Compressor</h2>
        <dl className="sheet-facts">
          <div><dt>Model</dt><dd>{compressor.brand} {compressor.model}</dd></div>
          <div><dt>Published delivery</dt><dd className="mono">{formatNumber(compressor.outputValue)} {compressor.outputUnit}</dd></div>
          <div><dt>Maker's wording</dt><dd>{compressor.outputLabel}</dd></div>
          <div><dt>Stated at</dt><dd className="mono">{compressor.pressurePsi} PSI</dd></div>
          <div><dt>Source</dt><dd className="sheet-url">{compressor.sourceUrl}</dd></div>
          <div><dt>Checked</dt><dd>{sheetDate(compressor.checkedDate)}</dd></div>
        </dl>
      </section>

      <section className="sheet-block">
        <h2>Side by side</h2>
        {result.status === 'comparable' ? (
          <p>At {result.pressurePsi} PSI: tool demand {formatNumber(result.demand)} CFM ({quantity > 1 ? `${quantity} tools, ` : ''}{mode === 'average' ? 'average' : 'at load'}); compressor output {formatNumber(result.compressorOutput)} CFM; difference {result.difference >= 0 ? '+' : '−'}{formatNumber(Math.abs(result.difference))} CFM.</p>
        ) : (
          <p>{result.explanation}</p>
        )}
        <p className="sheet-note">Compares published figures only. It does not size equipment: tank size, duty cycle, hose, regulator, and electrical supply are outside these figures.</p>
      </section>

      <section className="sheet-block sheet-notes">
        <h2>Your notes</h2>
        <p className="sheet-lines" aria-hidden="true" />
      </section>

      <p className="sheet-foot">{siteConfig.baseUrl.replace(/^https?:\/\//, '')}</p>
    </main>
  )
}

function ResultPanel({
  result,
  tool,
  compressor,
  quantity,
  mode,
  onCopy,
  copyStatus,
}: {
  result: ComparisonResult
  tool: ToolRecord
  compressor: CompressorRecord
  quantity: number
  mode: ConsumptionMode
  onCopy: () => void
  copyStatus: CopyStatus
}) {
  const sources = (
    <div className="result-sources">
      <a href={tool.sourceUrl} target="_blank" rel="noreferrer">Tool source <span aria-hidden="true">↗</span></a>
      <a href={compressor.sourceUrl} target="_blank" rel="noreferrer">Compressor source <span aria-hidden="true">↗</span></a>
      <span className="result-checked mono">checked {compressor.checkedDate}</span>
      <span className="result-source-label">Compressor figure published as: {compressor.outputLabel}</span>
    </div>
  )
  const shareControls = (
    <div className="result-actions">
      <button className="share-button" type="button" onClick={onCopy}>
        <span className="share-icon" aria-hidden="true" />
        Copy comparison link
      </button>
      <span className="share-status" role="status" aria-live="polite" data-state={copyStatus}>
        {copyStatus === 'copied' ? 'Link copied.' : copyStatus === 'failed' ? 'Could not copy the link. Copy it from the address bar.' : ''}
      </span>
    </div>
  )

  if (result.status !== 'comparable') {
    return (
      <div className={`result result-${result.status}`} aria-live="polite">
        <div className="result-badge-row">
          <span className="result-badge result-badge-stop">{result.status === 'not-comparable' ? 'Not comparable' : 'No figure'}</span>
          <span className="result-badge-text">the matcher gives no result</span>
        </div>
        <p className="result-title">
          {result.status === 'not-comparable' ? 'These two figures cannot be compared' : 'The source does not publish this figure'}
        </p>
        <p className="result-body">{result.explanation}</p>
        {sources}
        <p className="result-note">Compares published figures only. It does not size equipment.</p>
        {shareControls}
        <RetailerLinks result={result} tool={tool} compressor={compressor} />
      </div>
    )
  }

  const scale = Math.max(result.demand, result.compressorOutput, 1)
  const demandWidth = `${(result.demand / scale) * 100}%`
  const outputWidth = `${(result.compressorOutput / scale) * 100}%`
  const sign = result.difference >= 0 ? '+' : '−'
  const modeLabel = mode === 'average' ? 'average' : 'at load'

  return (
    <div className="result result-comparable" aria-live="polite">
      <div className="result-badge-row">
        <span className="result-badge">Difference</span>
        <span className="result-badge-text">both figures published at {result.pressurePsi} PSI</span>
      </div>
      <div className="result-figure">
        <span className="result-number">{sign}{formatNumber(Math.abs(result.difference))} CFM</span>
        <span className="result-caption">published output relative to published demand</span>
      </div>

      <dl className="bars">
        <div className="bar-row">
          <dt>
            <span>Tool demand, {quantity > 1 ? `${quantity} × ` : ''}{tool.brand} {tool.model} ({modeLabel})</span>
            <span className="bar-value mono">{formatNumber(result.demand)}</span>
          </dt>
          <dd><span className="bar-track"><span className="bar bar-demand" style={{ width: demandWidth }} /></span></dd>
        </div>
        <div className="bar-row">
          <dt>
            <span>Compressor output, {compressor.brand} {compressor.model}</span>
            <span className="bar-value mono">{formatNumber(result.compressorOutput)}</span>
          </dt>
          <dd><span className="bar-track"><span className="bar bar-output" style={{ width: outputWidth }} /></span></dd>
        </div>
      </dl>

      <p className="result-body">{result.explanation}</p>
      {sources}
      <p className="result-note">Compares published figures only. It does not size equipment.</p>
      {shareControls}
      <RetailerLinks result={result} tool={tool} compressor={compressor} />
    </div>
  )
}

export function App() {
  const [toolId, setToolId] = useState(tools[0]?.id ?? '')
  const [compressorId, setCompressorId] = useState(compressors[0]?.id ?? '')
  const [mode, setMode] = useState<ConsumptionMode>('average')
  const [quantity, setQuantity] = useState(1)
  const [hasCompared, setHasCompared] = useState(false)
  const [sharedEntryActive, setSharedEntryActive] = useState(false)
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  const [navOpen, setNavOpen] = useState(false)
  const [anchorVisible, setAnchorVisible] = useState(true)
  const restoredSharedResult = useRef(false)
  const [sheetState, setSheetState] = useState<SharedComparisonState | null>(null)

  const selectedTool = useMemo(() => tools.find((record) => record.id === toolId), [toolId])
  const selectedCompressor = useMemo(
    () => compressors.find((record) => record.id === compressorId),
    [compressorId],
  )
  const result = selectedTool && selectedCompressor
    ? compareToolToCompressor({ tool: selectedTool, compressor: selectedCompressor, mode, quantity })
    : null

  useEffect(() => {
    if (restoredSharedResult.current) return
    restoredSharedResult.current = true

    const shared = parseSharedComparison(window.location.search, sharedComparisonOptions)
    if (!shared) return

    if (siteConfig.newsletter.enabled && new URLSearchParams(window.location.search).get('view') === 'sheet') {
      setSheetState(shared)
      track('shop_sheet_viewed', { tool_id: shared.toolId, compressor_id: shared.compressorId })
      return
    }

    setToolId(shared.toolId)
    setCompressorId(shared.compressorId)
    setMode(shared.mode)
    setQuantity(shared.quantity)
    setHasCompared(true)
    setSharedEntryActive(true)
    const event = sharedResultViewedEvent(shared)
    track(event.name, event.details)
  }, [])

  function currentSharedState(): SharedComparisonState | null {
    return createSharedComparisonState(
      { toolId, compressorId, mode, quantity },
      sharedComparisonOptions,
    )
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setHasCompared(true)
    setCopyStatus('idle')

    const shared = currentSharedState()
    if (shared) {
      window.history.replaceState(
        window.history.state,
        '',
        browserPathWithSharedComparison(window.location.href, shared),
      )
      const analyticsEvent = utilityCompletedEvent(shared, sharedEntryActive ? 'shared_url' : 'form')
      track(analyticsEvent.name, analyticsEvent.details)
    } else {
      window.history.replaceState(
        window.history.state,
        '',
        browserPathWithoutSharedComparison(window.location.href),
      )
      track('utility_completed', {
        tool_id: toolId,
        tool_model: selectedTool?.model ?? '',
        compressor_id: compressorId,
        compressor_model: selectedCompressor?.model ?? '',
        mode,
        quantity,
        entry: 'form',
      })
    }
    setSharedEntryActive(false)
  }

  function resetComparison() {
    setHasCompared(false)
    setSharedEntryActive(false)
    setCopyStatus('idle')
    window.history.replaceState(
      window.history.state,
      '',
      browserPathWithoutSharedComparison(window.location.href),
    )
  }

  async function copyComparisonLink() {
    const shared = currentSharedState()
    if (!shared) {
      setCopyStatus('failed')
      return
    }

    const url = publicSharedComparisonUrl(siteConfig.baseUrl, shared)
    const writeText = navigator.clipboard?.writeText
      ? (value: string) => navigator.clipboard.writeText(value)
      : undefined
    const status = await copySharedComparisonLink(writeText, url)
    setCopyStatus(status)
    if (status === 'copied') {
      const event = resultLinkCopiedEvent(shared)
      track(event.name, event.details)
    }
  }

  function changeQuantity(next: number) {
    if (!Number.isFinite(next)) return
    setQuantity(Math.min(20, Math.max(1, Math.round(next))))
    resetComparison()
  }

  function changeTool(nextToolId: string) {
    const nextTool = tools.find((record) => record.id === nextToolId)
    setToolId(nextToolId)
    if (nextTool) setMode(modeAfterDirectToolSelection(nextTool, mode))
    resetComparison()
  }

  const averagePublished = selectedTool
    ? isConsumptionModePublished(selectedTool, 'average')
    : false
  const loadPublished = selectedTool
    ? isConsumptionModePublished(selectedTool, 'load')
    : false

  const featuredGuides = articleList.slice(0, 4)

  const sheetTool = sheetState ? tools.find((record) => record.id === sheetState.toolId) : undefined
  const sheetCompressor = sheetState ? compressors.find((record) => record.id === sheetState.compressorId) : undefined
  if (sheetState && sheetTool && sheetCompressor) {
    return <ShopSheet tool={sheetTool} compressor={sheetCompressor} mode={sheetState.mode} quantity={sheetState.quantity} />
  }

  const signupState = hasCompared ? currentSharedState() : null
  const sheetUrl = (() => {
    if (!signupState) return ''
    const url = new URL(publicSharedComparisonUrl(siteConfig.baseUrl, signupState))
    url.searchParams.set('view', 'sheet')
    url.hash = ''
    return url.toString()
  })()

  return (
    <div className="page">
      <header className="masthead">
        <div className="masthead-inner">
          <Wordmark />
          <button
            className="nav-toggle"
            type="button"
            aria-expanded={navOpen}
            aria-controls="primary-navigation"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setNavOpen((open) => !open)}
          >
            <span /><span /><span />
          </button>
          <nav className="masthead-nav" id="primary-navigation" aria-label="Primary navigation" data-open={navOpen ? 'true' : 'false'}>
            {siteConfig.navigation.map((item) => <a key={item.href} href={item.href}>{item.label}</a>)}
          </nav>
        </div>
      </header>

      {siteConfig.adPlaceholders ? <div className="ad-band ad-band-top"><AdSlot name="leaderboard" width={728} height={90} /></div> : null}

      <main>
        <section className="hero" id="matcher">
          <div className="hero-inner">
            <div className="hero-copy">
              <span className="sourced-badge">
                <span className="sourced-pill">Sourced</span>
                <span>{recordCount} records, each with a manufacturer link and checked date</span>
              </span>
              <h1>Compare air tool CFM with compressor output</h1>
              <p>Choose a tool and compressor. See their published figures at the same PSI, with links to both manufacturer pages.</p>
            </div>

            <form className="matcher-card" onSubmit={submit}>
              <div className="field">
                <label htmlFor="tool">Air tool</label>
                <div className="select-wrap">
                  <select id="tool" value={toolId} onChange={(event) => changeTool(event.target.value)}>
                    {tools.map((record) => (
                      <option key={record.id} value={record.id}>
                        {record.brand} {record.model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="compressor">Compressor</label>
                <div className="select-wrap">
                  <select id="compressor" value={compressorId} onChange={(event) => { setCompressorId(event.target.value); resetComparison() }}>
                    {compressors.map((record) => (
                      <option key={record.id} value={record.id}>{record.brand} {record.model}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field-row">
                <fieldset className="field segmented-field">
                  <legend>Tool figure</legend>
                  <div className="segmented" role="group">
                    <label className={`${mode === 'average' ? 'is-active ' : ''}${averagePublished ? '' : 'is-unavailable'}`.trim()}>
                      <input type="radio" name="mode" value="average" checked={mode === 'average'} disabled={!averagePublished} onChange={() => { setMode('average'); resetComparison() }} />
                      <span>Average</span>
                      {!averagePublished ? <span className="mode-availability">Not published</span> : null}
                    </label>
                    <label className={`${mode === 'load' ? 'is-active ' : ''}${loadPublished ? '' : 'is-unavailable'}`.trim()}>
                      <input type="radio" name="mode" value="load" checked={mode === 'load'} disabled={!loadPublished} onChange={() => { setMode('load'); resetComparison() }} />
                      <span>At load</span>
                      {!loadPublished ? <span className="mode-availability">Not published</span> : null}
                    </label>
                  </div>
                </fieldset>
                <div className="field qty-field">
                  <label htmlFor="quantity">Qty</label>
                  <div className="stepper">
                    <button type="button" aria-label="One fewer tool" onClick={() => changeQuantity(quantity - 1)} disabled={quantity <= 1}>−</button>
                    <input
                      id="quantity"
                      type="number"
                      min="1"
                      max="20"
                      step="1"
                      inputMode="numeric"
                      value={quantity}
                      onChange={(event) => changeQuantity(Number(event.target.value))}
                    />
                    <button type="button" aria-label="One more tool" onClick={() => changeQuantity(quantity + 1)} disabled={quantity >= 20}>+</button>
                  </div>
                </div>
              </div>
              <button className="button button-primary" type="submit">Compare the figures</button>
              <p className="matcher-note">Uses the selected manufacturer figures at their published pressure.</p>
            </form>
          </div>
        </section>

        <div className="content-grid">
          <div className="content-main">
            <div className="result-panel">
              {hasCompared && result && selectedTool && selectedCompressor ? (
                <ResultPanel
                  result={result}
                  tool={selectedTool}
                  compressor={selectedCompressor}
                  quantity={quantity}
                  mode={mode}
                  onCopy={copyComparisonLink}
                  copyStatus={copyStatus}
                />
              ) : (
                <div className="result result-empty">
                  <div className="result-badge-row">
                    <span className="result-badge result-badge-muted">Result</span>
                    <span className="result-badge-text">appears here after you compare</span>
                  </div>
                  <p className="result-title">Pick a tool and a compressor, then compare.</p>
                  <p className="result-body">Both published figures on one scale, the pressure they were stated at, and a link to each manufacturer page.</p>
                </div>
              )}
            </div>

            {sheetUrl ? <SheetSignup sheetUrl={sheetUrl} /> : null}

            {siteConfig.adPlaceholders ? <AdSlot name="after-result" width={336} height={280} note="in-content, after the result" className="ad-inline" /> : null}

            <section className="assets" aria-labelledby="assets-heading">
              <div className="section-head">
                <h2 id="assets-heading">Published CFM charts</h2>
                <a className="section-link" href="/records/">All records</a>
              </div>
              <div className="asset-grid">
                <a className="asset-card" href="/impact-wrench-cfm/">
                  <span className="asset-kicker">{impactWrenchCount} impact wrenches</span>
                  <span className="asset-title">Impact wrench CFM</span>
                  <span className="asset-copy">Average and at-load consumption at 90 PSI, model by model.</span>
                  <span className="asset-link">Open the chart</span>
                </a>
                <a className="asset-card" href="/compressor-output-90-psi/">
                  <span className="asset-kicker">{compressors.length} compressors</span>
                  <span className="asset-title">Compressor output at 90 PSI</span>
                  <span className="asset-copy">Published output, original unit labels, and manufacturer sources.</span>
                  <span className="asset-link">Open the chart</span>
                </a>
              </div>
            </section>

            <section className="guides" id="articles">
              <div className="section-head">
                <h2>Read before you compare</h2>
                <a className="section-link" href="/articles/">All guides</a>
              </div>
              <ol className="guide-list">
                {featuredGuides.map((article) => (
                  <li key={article.slug}>
                    <a className="guide" href={`/articles/${article.slug}/`}>
                      <span className="guide-image">
                        {article.heroImage ? <img src={article.heroImage} alt={article.heroImageAlt} loading="lazy" /> : null}
                      </span>
                      <span className="guide-copy">
                        <span className="guide-meta">{article.tags[0] ?? 'Guide'}{article.readTime ? `, ${article.readTime}` : ''}</span>
                        <span className="guide-title">{article.title}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
            </section>

            <section className="method" id="methodology">
              <div className="section-head">
                <h2>How the comparison works</h2>
              </div>
              <div className="method-columns">
                <div>
                  <h3>Manufacturer figures with source links</h3>
                  <p>Each tool and compressor record keeps the published value, model, pressure condition, manufacturer page, and checked date together.</p>
                </div>
                <div>
                  <h3>Pressure and labels checked before calculation</h3>
                  <p>The matcher calculates when both selected records publish CFM at the same PSI, then shows the figures and arithmetic used.</p>
                </div>
              </div>
            </section>

            {siteConfig.adPlaceholders ? <AdSlot name="end-of-page" width={970} height={250} note="end of page" className="ad-end" /> : null}
          </div>

          {siteConfig.adPlaceholders && anchorVisible ? (
            <div className="ad-anchor" role="complementary" aria-label="Advertisement">
              <div className="ad-anchor-inner">
                <span className="ad-slot-label">Advertisement</span>
                <span className="ad-slot-size mono">320 × 50</span>
              </div>
              <button className="ad-anchor-close" type="button" aria-label="Dismiss advertisement" onClick={() => setAnchorVisible(false)}>✕</button>
            </div>
          ) : null}

          <aside className="rail" aria-label="Sidebar">
            <div className="rail-sticky">
              {siteConfig.adPlaceholders ? <AdSlot name="rail" width={300} height={600} note="sticky rail" className="ad-rail" /> : null}
              <div className="directory-card">
                <h2>Records directory</h2>
                <dl>
                  {toolTypeCounts.map(([type, count]) => (
                    <div key={type}><dt>{type === 'Impact wrench' ? <a href="/impact-wrench-cfm/">{pluralizeToolType(type, count)}</a> : pluralizeToolType(type, count)}</dt><dd className="mono">{count}</dd></div>
                  ))}
                  <div><dt><a href="/compressor-output-90-psi/">Compressors</a></dt><dd className="mono">{compressors.length}</dd></div>
                  <div><dt>Manufacturers</dt><dd className="mono">{manufacturerCount}</dd></div>
                </dl>
                <a className="directory-link" href="/records/">Browse every record</a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-name">{siteConfig.name}</span>
            <span className="footer-line">Published figures, kept with their sources.</span>
          </div>
          <nav className="footer-nav" aria-label="Site pages">
            {siteConfig.navigation.map((item) => <a key={item.href} href={item.href}>{item.label}</a>)}
            {siteConfig.legalNavigation.map((item) => <a key={item.href} href={item.href}>{item.label}</a>)}
          </nav>
        </div>
      </footer>
    </div>
  )
}
